<template>
  <section class="tariff-row">
    <p v-if="title" class="tariff-row__title">{{ title }}</p>
    <div
      class="tariff-row__container"
      v-for="(row, rowIndex) in value"
      v-bind:key="rowIndex"
    >
      <div class="tariff-row__selects">
        <the-select
          option-title-key="name"
          option-value-key="id"
          v-bind:options="options"
          v-bind:value="row.selectedTariff.id"
          v-on:input="onSelectTariff(rowIndex, $event)"
        />
        <input-number v-if="row.isPacketModule" v-model="row.count" max="9999" />
        <the-select
          v-else
          optionTitleKey="period_title"
          optionValueKey="period_value"
          v-bind:options="row.prices"
          v-bind:value="row.selectedPrice.period_value"
          v-on:input="onSelectPeriod(rowIndex, $event)"
        />
      </div>
      <div class="tariff-row__price-wrapper">
        <span class="tariff-row__summ">Сумма:</span>
        <the-rub
          v-if="row.isPacketModule"
          class="tariff-row__price"
          v-bind:kop="row.cost * row.count * 100"
          currency="₸"
          show-kop="hide"
        />
        <template v-else>
          <del
            v-if="row.selectedPrice.original_price !== row.selectedPrice.final_price"
            class="tariff-row__price tariff-row__price--discount"
          >
            {{ row.selectedPrice.original_price_title }}
          </del>
          <span class="tariff-row__price">
            {{ row.selectedPrice.final_price_title }}
          </span>
        </template>
      </div>
      <button
        class="tariff-row__button-delete"
        type="button"
        v-on:click="onRemoveRow(rowIndex)"
      />
    </div>
    <button
      v-if="canAdd"
      class="tariff-row__button-add"
      type="button"
      v-on:click="onAddRow">Добавить</button>
  </section>
</template>

<script>
import TheSelect from '@/components/TheSelect.vue';
import InputNumber from './InputNumber.vue';
import TheRub from './TheRub.vue';

export default {
  components: { TheSelect, InputNumber, TheRub },
  computed: {
    canAdd() {
      if (this.selectOne) {
        return this.value.length < 1;
      }
      return this.items.length + this.packetModules.length > this.value.length;
    },
    selectedOptions() {
      return this.value.map(({ selectedTariff }) => selectedTariff.id);
    },
    options() {
      const selectedValues = this.selectedOptions;
      return [...this.items, ...this.packetModules].map(
        (item) => ({
          id: item.id,
          name: item.name,
          disabled: selectedValues.includes(item.id),
        }),
      );
    },
    packetModuleIds() {
      return this.packetModules.map(({ id }) => id);
    },
  },
  methods: {
    onAddRow() {
      const tariff = this.items.find(
        (candidate) => !this.selectedOptions.includes(candidate.id),
      );
      if (!tariff) {
        const packetModule = this.packetModules.find(
          (candidate) => !this.selectedOptions.includes(candidate.id),
        );
        if (!packetModule) {
          return;
        }
        const row = {
          cost: packetModule.cost,
          count: packetModule.initial_count,
          selectedTariff: packetModule,
          isPacketModule: true,
        };
        this.dispatchInput([...this.value, row]);
        return;
      }
      const row = {
        prices: tariff.prices,
        selectedPrice: tariff.prices[0],
        selectedTariff: tariff,
        isPacketModule: false,
      };
      this.dispatchInput([...this.value, row]);
    },
    selectPacketModule(rowIndex, tariffId) {
      const packetModule = this.packetModules.find(({ id }) => id === tariffId);
      if (!packetModule) { return; }
      const rows = this.value.map((row, index) => {
        if (index !== rowIndex) {
          return row;
        }
        return {
          cost: packetModule.cost,
          count: packetModule.initial_count,
          selectedTariff: packetModule,
          isPacketModule: true,
        };
      });
      this.dispatchInput(rows);
    },
    selectTariffPeriod(rowIndex, tariffId) {
      const tariff = this.items.find(({ id }) => id === tariffId);
      if (!tariff) { return; }
      const rows = this.value.map((row, index) => {
        if (index !== rowIndex) {
          return row;
        }
        return {
          prices: tariff.prices,
          selectedPrice: tariff.prices[0],
          selectedTariff: tariff,
        };
      });
      this.dispatchInput(rows);
    },
    onSelectTariff(rowIndex, tariffId) {
      const isPacketModule = this.packetModuleIds.includes(tariffId);
      if (isPacketModule) {
        this.selectPacketModule(rowIndex, tariffId);
      } else {
        this.selectTariffPeriod(rowIndex, tariffId);
      }
    },
    onSelectPeriod(rowIndex, priceId) {
      const rows = this.value.map((row, index) => {
        if (index !== rowIndex) {
          return row;
        }
        const price = row.prices.find(
          ({ period_value: value }) => value === priceId,
        );
        return {
          prices: row.selectedTariff.prices,
          selectedPrice: price,
          selectedTariff: row.selectedTariff,
        };
      });
      this.dispatchInput(rows);
    },
    dispatchInput(rows) {
      this.$emit('input', rows);
    },
    onRemoveRow(rowIndex) {
      const rows = this.value.filter((_, index) => rowIndex !== index);
      this.dispatchInput(rows);
    },
  },
  name: 'TariffRow',
  props: {
    items: { required: true, type: Array },
    packetModules: { default: () => [], type: Array },
    selectOne: { default: false, type: Boolean },
    title: { default: '', type: String },
    value: { required: true, type: Array },
  },
};
</script>

<style lang="scss">
.tariff-row {
  display: flex;
  flex-direction: column;
  row-gap: 30px;
  & &__title,
  & &__summ {
    display: inline-block;
    font-size: 1rem;
    font-weight: 500;
    color: var(--reading-color);
  }
  &__container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }
  &__selects {
    display: grid;
    grid-template-columns: 170px 140px;
    align-items: center;
    column-gap: 10px;
  }
  &__price-wrapper {
    display: flex;
    align-items: center;
    column-gap: 10px;
    margin-left: auto;
  }
  &__price {
    font-size: 18px;
    color: var(--action-color);
    &--discount {
      position: relative;
      font-size: 14px;
      font-weight: 500;
      text-decoration: none;
      color: var(--placeholder-color);
      &::before {
        content: "";
        width: 100%;
        height: 95%;
        position: absolute;
        top: 0;
        left: 0;
        background: linear-gradient(
          to top right,
          transparent calc(50% - 1px),
          var(--placeholder-color),
          transparent calc(50% + 1px)
        );
      }
    }
  }
  &__button-delete {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 31px;
    height: 31px;
    background: no-repeat url("@/assets/img/cross/cross-grey.svg");
    background-position: 50%;
    background-size: 17px 17px;
    transition: background-image 0.3s;
    &:hover {
      background-image: url("@/assets/img/cross/cross-blue.svg");
    }
  }
  &__button-add {
    margin-right: auto;
    color: var(--action-color);
    transition: color 0.3s;
    &:hover {
      color: #0076C4;
    }
  }
}

@media screen and (max-width: 660px) {
  .tariff-row {
    &__container {
      display: grid;
      grid-template-columns: 1fr 1fr;
    }
    &__price-wrapper {
      margin-left: 0;
    }
    &__button-delete {
      grid-column: 2/3;
      grid-row: 1/2;
      justify-self: end;
    }
  }
}
</style>
